import React, { useState, useEffect, useMemo } from "react";
import { Card, Row, Col, DatePicker, Space, Table, Tag, Select, Modal, Descriptions, Statistic, Typography, Divider, Button } from "antd";
import { PhoneOutlined, InfoCircleOutlined, ExclamationCircleOutlined, ToolOutlined, CloseCircleOutlined, FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
 
import api from "../api/Api";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const ListagemChamadasRegistadas = () => {
  const inicioMes = dayjs().startOf("month");
  const fimMes = dayjs().endOf("month");

  const [datas, setDatas] = useState([inicioMes, fimMes]);
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState([]);
  const [tipoFiltro, setTipoFiltro] = useState(null);
  const [provinciaFiltro, setProvinciaFiltro] = useState(null);
  const [dashboard, setDashboard] = useState({});
  const [detalheVisible, setDetalheVisible] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const [totalChamadas, setTotalChamadas] = useState(0);
  const [totalInfo, settotalInfo] = useState(0);
  const [totalSolicitacao, settotalSolicitacao] = useState(0);
  const [totalReclamacao, settotalReclamacao] = useState(0);
  const [totalIndisponibilidade, settotalIndisponibilidade] = useState(0);

  useEffect(() => {
    if (datas && datas[0] && datas[1]) {
      fetchChamadas();
      fetchDashboard();
    }
  }, [datas]);

  const fetchChamadas = async () => {
    try {
      setLoading(true);
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
      };
      const response = await api.ListChamadaRegistada(payload);
      if (Array.isArray(response)) setChamadas(response);
    } catch (error) {
      console.error("Erro ao buscar chamadas registadas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const payload = {
        dataInicio: datas[0].format("YYYY-MM-DD"),
        dataFim: datas[1].format("YYYY-MM-DD"),
      };
      const response = await api.ListDashboardChamadaRegistada(payload);
      if (response && typeof response === "object") {
        setTotalChamadas(response.total);
        settotalInfo(response.informacao);
        settotalSolicitacao(response.solicitacao);
        settotalReclamacao(response.reclamacao);
        settotalIndisponibilidade(response.indisponibilidde);
      }
    } catch (error) {
      console.error("Erro ao buscar dashboard:", error);
    }
  };

  // Função para exportar PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Configurações do documento
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    
    // Cabeçalho do PDF
    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text("RELATÓRIO DE CHAMADAS REGISTADAS", pageWidth / 2, 20, { align: 'center' });
    
    // Informações do período
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Período: ${datas[0].format('DD/MM/YYYY')} - ${datas[1].format('DD/MM/YYYY')}`, margin, 35);
    doc.text(`Data de geração: ${dayjs().format('DD/MM/YYYY HH:mm')}`, margin, 42);
    
    // Filtros aplicados
    let filtrosText = "Filtros aplicados: ";
    const filtros = [];
    if (tipoFiltro) filtros.push(`Tipo: ${tipoFiltro}`);
    if (provinciaFiltro) filtros.push(`Província: ${provinciaFiltro}`);
    
    filtrosText += filtros.length > 0 ? filtros.join(', ') : 'Nenhum filtro aplicado';
    doc.text(filtrosText, margin, 49);
    
    // Resumo estatístico
    doc.setFontSize(14);
    doc.setTextColor(44, 62, 80);
    doc.text("RESUMO ESTATÍSTICO", margin, 65);
    
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let yPosition = 75;
    
    const statsData = [
      [`Total de Chamadas:`, totalChamadas],
      [`Informações:`, totalInfo],
      [`Reclamações:`, totalReclamacao],
      [`Solicitações:`, totalSolicitacao],
      [`Indisponibilidades:`, totalIndisponibilidade],
      [`Chamadas Filtradas:`, filteredChamadas.length]
    ];
    
    statsData.forEach(([label, value]) => {
      doc.text(`${label} ${value}`, margin, yPosition);
      yPosition += 6;
    });
    
    // Tabela de dados
    if (filteredChamadas.length > 0) {
      yPosition += 10;
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text("DETALHES DAS CHAMADAS", margin, yPosition);
      
      // Preparar dados para a tabela
      const tableData = filteredChamadas.map((chamada, index) => [
        index + 1,
        chamada.nome || 'N/A',
        chamada.provincia || 'N/A',
        chamada.municipio || 'N/A',
        dayjs(chamada.data).format('DD/MM/YYYY HH:mm'),
        chamada.tipo_chamada || 'N/A',
        chamada.ocorrencia ? (chamada.ocorrencia.length > 50 ? chamada.ocorrencia.substring(0, 50) + '...' : chamada.ocorrencia) : 'N/A'
      ]);
      
      // Configurar a tabela
      doc.autoTable({
        startY: yPosition + 5,
        head: [['#', 'Nome', 'Província', 'Município', 'Data', 'Tipo', 'Ocorrência']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [44, 62, 80],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 10 }, // #
          1: { cellWidth: 25 }, // Nome
          2: { cellWidth: 25 }, // Província
          3: { cellWidth: 25 }, // Município
          4: { cellWidth: 30 }, // Data
          5: { cellWidth: 25 }, // Tipo
          6: { cellWidth: 40 }  // Ocorrência
        },
        didDrawPage: (data) => {
          // Rodapé em cada página
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Página ${doc.internal.getNumberOfPages()}`, 
            pageWidth / 2, 
            doc.internal.pageSize.getHeight() - 10, 
            { align: 'center' }
          );
        }
      });
    } else {
      yPosition += 15;
      doc.setFontSize(12);
      doc.setTextColor(150, 150, 150);
      doc.text("Nenhuma chamada encontrada com os filtros aplicados.", pageWidth / 2, yPosition, { align: 'center' });
    }
    
    // Salvar o PDF
    const fileName = `chamadas_registadas_${dayjs().format('YYYY-MM-DD_HH-mm')}.pdf`;
    doc.save(fileName);
  };

  const filteredChamadas = chamadas.filter((c) => {
    return (
      (!tipoFiltro || c.tipo_chamada === tipoFiltro) &&
      (!provinciaFiltro || c.provincia === provinciaFiltro)
    );
  });

  const getTipoIcon = (tipo) => {
    const icons = {
      "Informação": <InfoCircleOutlined />,
      "Reclamação": <ExclamationCircleOutlined />,
      "Solicitação": <ToolOutlined />,
      "Indisponibilidade": <CloseCircleOutlined />,
    };
    return icons[tipo] || <PhoneOutlined />;
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Província",
      dataIndex: "provincia",
      key: "provincia",
      width: 120,
    },
    {
      title: "Município",
      dataIndex: "municipio",
      key: "municipio",
      width: 120,
    },
    {
      title: "Data",
      dataIndex: "data",
      key: "data",
      width: 150,
      render: (text) => (
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {dayjs(text).format("DD/MM/YYYY HH:mm")}
        </Text>
      ),
    },
    {
      title: "Tipo de Chamada",
      dataIndex: "tipo_chamada",
      key: "tipo_chamada",
      width: 140,
      render: (tipo) => {
        const cores = {
          Reclamação: "volcano",
          Solicitação: "blue",
          Informação: "green",
          Indisponibilidade: "purple",
        };
        return (
          <Tag 
            color={cores[tipo]} 
            icon={getTipoIcon(tipo)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '12px'
            }}
          >
            {tipo}
          </Tag>
        );
      },
    },
    {
      title: "Ação",
      key: "acao",
      width: 100,
      render: (_, record) => (
        <a
          onClick={() => {
            setDetalhe(record);
            setDetalheVisible(true);
          }}
          style={{ fontWeight: 500 }}
        >
          Ver Detalhes
        </a>
      ),
    },
  
  ];

  const stats = useMemo(() => [
    { 
      title: "Total", 
      value: totalChamadas || 0, 
      color: "#1890ff",
      icon: <PhoneOutlined />,
      prefix: null
    },
    { 
      title: "Informações", 
      value: totalInfo || 0, 
      color: "#52c41a",
      icon: <InfoCircleOutlined />,
      prefix: null
    },
    { 
      title: "Reclamações", 
      value: totalReclamacao || 0, 
      color: "#ff4d4f",
      icon: <ExclamationCircleOutlined />,
      prefix: null
    },
    { 
      title: "Solicitações", 
      value: totalSolicitacao || 0, 
      color: "#faad14",
      icon: <ToolOutlined />,
      prefix: null
    },
    { 
      title: "Indisponibilidades", 
      value: totalIndisponibilidade || 0, 
      color: "#722ed1",
      icon: <CloseCircleOutlined />,
      prefix: null
    },
  ], [totalChamadas, totalInfo, totalReclamacao, totalSolicitacao, totalIndisponibilidade]);

  return (
    <div
      style={{
        padding: "20px",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
      }}
    >
      {/* Cabeçalho */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: "#2c3e50", fontWeight: 700 }}>
              Chamadas Registadas
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Gerencie e visualize todas as chamadas do sistema
            </Text>
          </div>
          
          <Space size="middle" wrap>
            
            
            <div>
              <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                Período
              </Text>
              <RangePicker
                format="DD/MM/YYYY"
                value={datas}
                onChange={(values) => setDatas(values)}
                style={{ borderRadius: "8px", minWidth: '240px' }}
                size="middle"
              />
            </div>
            
            <div>
              <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                Tipo
              </Text>
              <Select
                placeholder="Filtrar por tipo"
                allowClear
                onChange={(value) => setTipoFiltro(value)}
                style={{ width: 180 }}
                size="middle"
              >
                <Option value="Reclamação">Reclamação</Option>
                <Option value="Solicitação">Solicitação</Option>
                <Option value="Informação">Informação</Option>
                <Option value="Indisponibilidade">Indisponibilidade</Option>
              </Select>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>
                Província
              </Text>
              <Select
                placeholder="Filtrar por província"
                allowClear
                onChange={(value) => setProvinciaFiltro(value)}
                style={{ width: 180 }}
                size="middle"
              >
                {[...new Set(chamadas.map((c) => c.provincia))].map((prov, i) => (
                  <Option key={i} value={prov}>
                    {prov}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
        </div>
      </div>

      {/* Cards de resumo */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        {stats.map((item, index) => (
          <Col xs={24} sm={12} md={8} lg={4} key={index}>
            <Card
              bordered={false}
              style={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}08 100%)`,
                border: `1px solid ${item.color}20`,
                height: '100%',
              }}
              bodyStyle={{ padding: '16px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    backgroundColor: item.color,
                    color: "white",
                    borderRadius: "12px",
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: "13px", color: '#666', fontWeight: 500 }}>
                    {item.title}
                  </Text>
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: item.color, lineHeight: 1.2 }}>
                    {item.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabela */}
      <Card
        bordered={false}
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ padding: '20px 20px 0 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>
              Lista de Chamadas
            </Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                Total: {filteredChamadas.length}
              </Tag>
              <Button 
                type="text" 
                icon={<FilePdfOutlined />}
                onClick={exportToPDF}
                style={{ color: '#dc3545' }}
                size="small"
              >
                PDF
              </Button>
            </div>
          </div>
        </div>
        
        <div style={{ maxHeight: "calc(100vh - 400px)", overflow: "auto" }}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredChamadas}
            loading={loading}
            pagination={{ 
              pageSize: 11, 
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} itens`
            }}
            style={{ padding: '0 20px 20px 20px' }}
            scroll={{ x: 800 }}
          />
        </div>
      </Card>

      {/* Modal de Detalhes */}
      <Modal
        open={detalheVisible}
        title={
          <div>
            <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Detalhes da Chamada
          </div>
        }
        footer={null}
        onCancel={() => setDetalheVisible(false)}
        width={700}
        style={{ top: 20 }}
      >
        {detalhe && (
          <Descriptions 
            bordered 
            column={1} 
            size="middle"
            labelStyle={{ 
              fontWeight: 600, 
              background: '#fafafa',
              width: '140px'
            }}
          >
            <Descriptions.Item label="ID">{detalhe.id}</Descriptions.Item>
            <Descriptions.Item label="Nome">{detalhe.nome}</Descriptions.Item>
            <Descriptions.Item label="Província">{detalhe.provincia}</Descriptions.Item>
            <Descriptions.Item label="Município">{detalhe.municipio}</Descriptions.Item>
            <Descriptions.Item label="Tipo de Chamada">
              <Tag color={
                {
                  'Reclamação': 'volcano',
                  'Solicitação': 'blue',
                  'Informação': 'green',
                  'Indisponibilidade': 'purple'
                }[detalhe.tipo_chamada]
              }>
                {detalhe.tipo_chamada}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Data">
              {dayjs(detalhe.data).format("DD/MM/YYYY HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Ocorrência" span={1}>
              <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {detalhe.ocorrencia}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Observação" span={1}>
              <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {detalhe.obs || 'Nenhuma observação'}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Plano">{detalhe.plano}</Descriptions.Item>
            <Descriptions.Item label="Pacote">{detalhe.pacote}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ListagemChamadasRegistadas;